<script>
    import { apiMethods } from "../../../Services/index.svelte";

    import { user } from "../../../stores";

    import {
        SvelteTable,
        LoadingModal,
        CardLayout,
    } from "../../../Components/Common/index.svelte";

    import {
        getFormatDate,
        getFormatDateAndTime,
        downloadAccountReport,
        showError,
        showException,
        splitCamelCase,
        getText,
        getDaysDifferFrom,
        getYYYYMMDD,
    } from "../../../Utils/utilFunctions.svelte";
    import { DirectUserRoles } from "../../../Utils/utilEnums.svelte";

    export let accountId;

    $: if (accountId) loadAccountReports(accountId);

    const loadAccountReports = async (id) => {
        if (!id) return;
        isLoading = true;
        await apiMethods.getMethod
            .getAccountReportsById(id)
            .then((res) => _handleGetAccountReports(res))
            .catch((err) => showError(err));

        isLoading = false;
    };

    $: isUserAdmin = $user.role == 100;

    let reports = [],
        isLoading = false,
        isDownloadingAccountReport = false,
        isRegeneratingAccountReport = false;

    const columns = [
        // {
        //     key: "id",
        //     title: "Report ID",
        //     headerClass: "text-left px-3 cursor-default",
        //     class: "text-left px-3 cursor-pointer",
        //     renderValue: (v) => getText(v.id),
        // },
        {
            key: "createDate",
            title: "Create Date",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.createdDate, false, true),
        },
        {
            key: "period",
            title: "For Period",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.formattedDate, "-"),
        },
        {
            key: "type",
            title: "Type",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) =>
                splitCamelCase(v.typeName?.toString().replace(/^Account/, "")),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-right px-3 cursor-default rounded-tr-xl",
            class: "text-right px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
        },
    ];

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "actions") {
            if (className.includes("downloadIcon"))
                downloadAccountReport(rowData.id);
            else if (className.includes("reGenerateIcon"))
                regenerateAccountReport(rowData);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const regenerateAccountReport = async (data) => {
        const { type, id, doctorId, formattedDate } = data;

        if (!formattedDate) {
            showError(
                "Unable to regenerate report as period of selected report is missing...",
            );
            return;
        }

        const [day, month, year] = formattedDate.split(" - ")[1].split("/");

        if (!day || !month || !year) {
            showError("Invalid report period...");
            return;
        }

        const regenerateObj = {
            type,
            entityId: id,
            doctorId,
            patientId: 0,
            patientEncounterId: 0,
            invoiceDetails: {
                createdDate: getDaysDifferFrom(
                    1,
                    getYYYYMMDD(`${year}-${month}-${day}`),
                ),
            },
        };

        isRegeneratingAccountReport = true;

        await apiMethods.postMethod
            .regeneratePdfFile(regenerateObj)
            .then((res) => {
                if (res.id) {
                    downloadAccountReport(res.id);
                    loadAccountReports(accountId);
                } else
                    showError(
                        "Failed to re-generate this report PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Failed to re-generate this report PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isRegeneratingAccountReport = false;
    };

    // HELPER FUNCTIONS
    const _handleGetAccountReports = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load account reports, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            reports = res
                .map((report) => {
                    const fileNameObj = _getFormatPeriod(report.fileName);
                    return {
                        ...report,
                        formattedDate: fileNameObj
                            ? fileNameObj.formattedDate
                            : "",
                        period: fileNameObj ? fileNameObj.period : "",
                        startDate: fileNameObj ? fileNameObj.startDate : null,
                    };
                })
                .sort((a, b) => {
                    if (a.formattedDate === "" && b.formattedDate === "") {
                        if (a.startDate && b.startDate) {
                            if (a.startDate < b.startDate) return -1;
                            if (a.startDate > b.startDate) return 1;
                        }
                        if (a.period === "Quarterly" && b.period === "Annual")
                            return -1;
                        if (a.period === "Annual" && b.period === "Quarterly")
                            return 1;
                        return 0;
                    }

                    if (!a.formattedDate) return -1;
                    if (!b.formattedDate) return 1;

                    const _extractEndDate = (date) => {
                        const parts = date.split(" - ");
                        if (parts.length && parts.length == 2) {
                            const [dd, mm, yyyy] = parts[1].split("/");
                            return new Date(`${yyyy}-${mm}-${dd}`);
                        }
                        return null;
                    };

                    const dateA = _extractEndDate(a.formattedDate);
                    const dateB = _extractEndDate(b.formattedDate);

                    if (dateA && dateB) {
                        if (dateA < dateB) return -1;
                        else if (dateA > dateB) return 1;
                        else if (
                            a.period == "Quarterly" &&
                            b.period == "Annual"
                        )
                            return -1;
                        else if (
                            a.period == "Annual" &&
                            b.period == "Quarterly"
                        )
                            return 1;
                        else return 0;
                    }

                    return 0;
                });
        }
    };

    const _getFormatDate = (date, isExpired, isDateOnly) => {
        if (isExpired) return "Expired";

        if (isDateOnly) return getFormatDate(date);

        return getFormatDateAndTime(date);
    };

    const _getFormatPeriod = (fileName) => {
        const fileNameRegex =
            /^(Quarterly|Annual)_Report_(\d{2})-(\d{2})-(\d{4})_(\d{2})-(\d{2})-(\d{4})\.pdf$/;

        if (!fileName || !fileNameRegex.test(fileName)) return "";

        const [, period, DD1, MM1, YYYY1, DD2, MM2, YYYY2] =
            fileName.match(fileNameRegex);

        return {
            period, // Quarterly or Annual
            formattedDate: `${DD1}/${MM1}/${YYYY1} - ${DD2}/${MM2}/${YYYY2}`,
            startDate: new Date(`${YYYY1}-${MM1}-${DD1}`), // for period sorting
        };
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-end gap-x-2">
                ${
                    isUserAdmin
                        ? `<div class="reGenerateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Re-generate Report PDF">
                            <i class="reGenerateIcon fas fa-arrows-rotate"></i>
                        </div>`
                        : ""
                }

                <div class="downloadIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Download Report PDF">
                    <i class="downloadIcon fas fa-download"></i>
                </div>
            </div>`;
    };
</script>

<CardLayout title="Doctor Reports">
    {#if reports.length}
        <div class="flex flex-1 overflow-auto">
            <SvelteTable
                {columns}
                rows={reports}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
                clickColFunc={({ detail: { event, row, key } }) => {
                    handleClickCol(event, row, key);
                }}
            />
        </div>
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>
                    No reports {$user.role == DirectUserRoles.Doctor
                        ? ""
                        : "currently under this doctor"}
                </p>
            </div>
        </div>
    {/if}
</CardLayout>

{#if isDownloadingAccountReport}
    <LoadingModal titleText="Downloading Report PDF..." />
{/if}

{#if isRegeneratingAccountReport}
    <LoadingModal titleText="Regenerating Report PDF..." />
{/if}
